// Visual (session) unread state: the badge/count source of truth for the sidebar,
// workspace rail, tab title, and Electron badge. This module never touches the
// network — the durable read cursor lives in `read_cursor.js` and the policy for
// when either advances lives in `channelReadLifecycle.js`.
//
// The model:
//   counts   = Dexie messages/mentions newer than each channel's cutoff, where
//              cutoff = max(server marker, session overlay); own-authored and
//              optimistic rows never count.
//   overlay  = an in-memory per-channel cursor stamped when the viewer reads
//              (enter/focus/blur) — clears badges instantly, reset on reload.
//   visit    = a per-channel snapshot taken on entry (marker position + whether
//              anything was unread) that the feed's NEW divider latches from.
import { useMemo, useRef, useSyncExternalStore } from 'react';

import Dexie, { liveQuery } from 'dexie';
import { encodeTime, ulid } from 'ulid';

import { logDebug, logError } from '../lib/logger';
import { sortULIDs } from '../lib/stringUtils';

import db from './db';

let started = false;
const SIDEBAR_UNREAD_DEBUG_TAG = 'unreads-sidebar-v2';
const MAX_DEBUG_CHANNELS = 50;
const EMPTY_UNREAD_SIGNAL = Object.freeze({ messages: 0, mentions: 0 });
let lastSidebarDebugDigest = null;

// external store state
let storeVersion = 0;
let profilesByWorkspaceId = new Map();
let markersByChannelId = new Map();
let unreadMessagesByChannelId = new Map();
let unreadMentionsByChannelId = new Map();
let overlayLastReadSortOrderById = new Map();
// per-visit divider baselines: marker position + whether anything was unread when the
// viewer entered the channel, captured before the overlay wipes the live counts.
let entryDividerById = new Map();
const pendingVisitChannelIds = new Set();
let hasComputedOnce = false;

// Dexie's liveQuery only re-runs on writes to the tables it queried; overlay cursor
// movement is in-memory, so it schedules a manual recompute instead. The sequence
// guard keeps a slow older run from clobbering a newer one.
let recomputeSeq = 0;
let lastAppliedRecomputeSeq = 0;
let manualRecomputeQueued = false;

// track external listeners
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function bumpAndNotify() {
  storeVersion++;
  notify();
}

async function computeUnreadData() {
  const data = { markers: [], messages: {}, mentions: {}, entryDividers: {}, debugChannels: {} };

    // markers are the source of truth — derive workspaces + profiles from them
    data.markers = await db.channel_read_markers.toArray();
    const workspaceIds = [...new Set(data.markers.map((m) => m.workspace_id))];

    // markers are per-user, so each [workspace_id, profile_id] pair is "you" in that
    // workspace. bulkGet by compound PK instead of loading every profile in the workspace.
    // The same fact makes marker profile_ids the full set of the viewer's identities per
    // workspace — used below so no own-authored message ever counts as unread, even in
    // multi-identity workspaces where a channel's surviving marker row belongs to a
    // different identity than the one that sent.
    // Malformed rows are skipped loudly instead of thrown on: one poisoned row would
    // otherwise kill the liveQuery subscription and freeze every unread indicator.
    const myProfileKeys = [];
    const seenProfileKeys = new Set();
    const ownProfileIdsByWorkspaceId = new Map();
    for (const marker of data.markers) {
      if (marker.workspace_id == null || marker.profile_id == null) {
        continue;
      }
      const key = `${marker.workspace_id}:${marker.profile_id}`;
      if (seenProfileKeys.has(key)) continue;
      seenProfileKeys.add(key);
      myProfileKeys.push([marker.workspace_id, marker.profile_id]);

      const ownProfileIds = ownProfileIdsByWorkspaceId.get(marker.workspace_id) || new Set();
      ownProfileIds.add(String(marker.profile_id));
      ownProfileIdsByWorkspaceId.set(marker.workspace_id, ownProfileIds);
    }
    const profiles = (await db.profiles.bulkGet(myProfileKeys)).filter(Boolean);
    const channels = await db.channels.where('workspace_id').anyOf(workspaceIds).toArray();

    // profiles is already prefiltered to "me" per workspace, so key by workspace_id and
    // stash both the profile_id and the day-zero sort order (lowest sort_order at join).
    const nextProfilesByWorkspaceId = new Map();
    for (const profile of profiles) {
      let dayZero;
      try {
        dayZero = `${encodeTime(profile.created_at * 1000)}0000000000000000`;
      } catch {
        continue;
      }
      nextProfilesByWorkspaceId.set(profile.workspace_id, {
        profileId: profile.id,
        dayZero,
      });
    }
    profilesByWorkspaceId = nextProfilesByWorkspaceId;

    const channelByWorkspaceAndId = new Map();
    for (const c of channels) {
      if (c.id == null || String(c.id).startsWith('skeleton_')) continue;
      channelByWorkspaceAndId.set(`${c.workspace_id}:${c.id}`, c);
    }

    // Server only creates a ChannelReadMarker when a channel is first marked-as-read
    // (Channel#mark_as_read_for). Channels you can see but never opened have none, so
    // the count loop below would skip them entirely. Synthesize a marker at day-zero.
    // TODO: backend should upsert a CRM in Channel#add_member! so every visible channel
    // has a real marker and this fallback becomes dead code.
    //
    // Gap: a workspace with zero real markers (brand-new membership, nothing ever read)
    // has no profile row in profilesByWorkspaceId — bulkGet only loaded the keys pulled
    // from markers — so its channels are skipped until any CRM in that workspace exists.
    // Acceptable for now; the only real-world hit is "first unread in a freshly joined
    // workspace."
    const markerKeys = new Set(data.markers.map((m) => `${m.workspace_id}:${m.channel_id}`));
    for (const channel of channels) {
      if (channel.id == null || String(channel.id).startsWith('skeleton_')) continue;
      if (markerKeys.has(`${channel.workspace_id}:${channel.id}`)) continue;

      const me = profilesByWorkspaceId.get(channel.workspace_id);
      if (!me) continue;

      data.markers.push({
        workspace_id: channel.workspace_id,
        channel_id: channel.id,
        profile_id: me.profileId,
        last_read_sort_order: me.dayZero,
      });
    }

    // count unread messages/mentions for each marker (real or synthetic)
    for (const marker of data.markers) {
      const channelId = marker.channel_id;
      const channel = channelByWorkspaceAndId.get(`${marker.workspace_id}:${channelId}`);
      if (!channel) continue;

      try {
      const serverLastReadSortOrder = marker.last_read_sort_order;
      if (marker.profile_id == null || (serverLastReadSortOrder == null && overlayLastReadSortOrderById.get(channelId) == null)) {
        continue;
      }


      const overlayLastReadSortOrder = overlayLastReadSortOrderById.get(channelId);
      const useOverlayLastReadSortOrder = sortULIDs(overlayLastReadSortOrder, serverLastReadSortOrder) === 1;
      // guard above ensures at least one cursor exists; never build a Dexie range from null
      const cutoff = useOverlayLastReadSortOrder ? overlayLastReadSortOrder : (serverLastReadSortOrder ?? overlayLastReadSortOrder);

      // The viewer's own sends never count as unread: not messages authored by any of
      // their workspace identities, and not optimistic rows (always the viewer's own
      // in-flight sends, which can briefly lack an author).
      const ownProfileIds = ownProfileIdsByWorkspaceId.get(marker.workspace_id);
      const isOwnProfileId = (profileId) =>
        ownProfileIds ? ownProfileIds.has(String(profileId)) : String(profileId) === String(marker.profile_id);

      const unreadMessagesCollection = (fromSortOrder = cutoff) =>
        db.messages
          .where('[channel_id+sort_order]')
          .between([channelId, fromSortOrder], [channelId, Dexie.maxKey], false, true)
          .filter((msg) => msg.type == null && msg.pseudotype !== 'optimistic' && !isOwnProfileId(msg.author_id));
      const unreadMentionsCollection = (fromSortOrder = cutoff) =>
        db.mentions
          .where('[workspace_id+source_id+recipient_id+sort_order]')
          .between(
            [marker.workspace_id, channelId, marker.profile_id, fromSortOrder],
            [marker.workspace_id, channelId, marker.profile_id, Dexie.maxKey],
            false,
            true,
          )
          .filter((msg) => !isOwnProfileId(msg.sender_id));

      data.messages[channelId] = await unreadMessagesCollection().count();
      data.mentions[channelId] = await unreadMentionsCollection().count();

      // A visit that began before the first recompute (e.g. deep link straight into a
      // channel) could not snapshot live counts; derive its divider baseline here from
      // the server marker, ignoring the visit's own overlay cutoff.
      if (pendingVisitChannelIds.has(channelId)) {
        let entryHasUnread;
        if (cutoff === serverLastReadSortOrder) {
          entryHasUnread = data.messages[channelId] > 0 || data.mentions[channelId] > 0;
        } else {
          entryHasUnread =
            (await unreadMessagesCollection(serverLastReadSortOrder).first()) !== undefined ||
            (await unreadMentionsCollection(serverLastReadSortOrder).first()) !== undefined;
        }
        data.entryDividers[channelId] = { sortOrder: serverLastReadSortOrder ?? null, hasUnread: entryHasUnread };
      }

      const messageSamples =
        data.messages[channelId] > 0
          ? (await unreadMessagesCollection().limit(5).toArray()).map((message) => ({
              id: message.id,
              workspaceId: message.workspace_id,
              channelId: message.channel_id,
              sortOrder: message.sort_order,
              authorId: message.author_id || null,
              type: message.type || null,
              pseudotype: message.pseudotype || null,
              timestamp: message.timestamp || null,
            }))
          : [];
      const mentionSamples =
        data.mentions[channelId] > 0
          ? (await unreadMentionsCollection().limit(5).toArray()).map((mention) => ({
              id: mention.id,
              workspaceId: mention.workspace_id,
              sourceId: mention.source_id,
              recipientId: mention.recipient_id,
              sortOrder: mention.sort_order,
              senderId: mention.sender_id || null,
              pseudotype: mention.pseudotype || null,
            }))
          : [];

      data.debugChannels[channelId] = {
        workspaceId: marker.workspace_id,
        channelId,
        markerId: marker.id || null,
        profileId: marker.profile_id || null,
        syntheticMarker: !marker.id,
        serverLastReadSortOrder,
        overlayLastReadSortOrder: overlayLastReadSortOrder || null,
        cutoff,
        cutoffSource: useOverlayLastReadSortOrder ? 'overlay' : 'server',
        messages: data.messages[channelId] || 0,
        mentions: data.mentions[channelId] || 0,
        messageSamples,
        mentionSamples,
      };
      } catch {
        // One bad row must degrade to a skipped channel, never a dead subscription.
      }
    }

    return data;
}

function applyUnreadData(data, seq) {
      if (seq <= lastAppliedRecomputeSeq) return;
      lastAppliedRecomputeSeq = seq;
      hasComputedOnce = true;

      // rebuild the maps from data.markers so channels whose markers disappeared
      // (purge, channel deletion, workspace leave) drop their stale counts.
      const nextMarkers = new Map();
      const nextMessages = new Map();
      const nextMentions = new Map();
      for (const marker of data.markers) {
        nextMarkers.set(marker.channel_id, marker);
        nextMessages.set(marker.channel_id, data.messages[marker.channel_id] || 0);
        nextMentions.set(marker.channel_id, data.mentions[marker.channel_id] || 0);
      }
      markersByChannelId = nextMarkers;
      unreadMessagesByChannelId = nextMessages;
      unreadMentionsByChannelId = nextMentions;

      // fill visit baselines that were requested before any recompute had run;
      // a live beginChannelVisit snapshot in the meantime wins.
      for (const [channelId, entry] of Object.entries(data.entryDividers)) {
        if (!pendingVisitChannelIds.has(channelId)) continue;
        pendingVisitChannelIds.delete(channelId);
        entryDividerById.set(channelId, entry);
      }

      const nonZero = [];
      for (const marker of data.markers) {
        const channelId = marker.channel_id;
        const messages = data.messages[channelId] || 0;
        const mentions = data.mentions[channelId] || 0;
        if (messages > 0 || mentions > 0) {
          nonZero.push({ channelId, messages, mentions, debug: data.debugChannels[channelId] || null });
        }
      }

      nonZero.sort((a, b) => {
        if (b.mentions !== a.mentions) return b.mentions - a.mentions;
        if (b.messages !== a.messages) return b.messages - a.messages;
        return String(a.channelId).localeCompare(String(b.channelId));
      });

      const digestEntries = nonZero.slice(0, MAX_DEBUG_CHANNELS);
      const debugDigest = JSON.stringify({
        markerCount: data.markers.length,
        nonZeroCount: nonZero.length,
        nonZero: digestEntries.map((entry) => [entry.channelId, entry.messages, entry.mentions]),
        truncated: nonZero.length > MAX_DEBUG_CHANNELS,
      });

      if (debugDigest !== lastSidebarDebugDigest) {
        lastSidebarDebugDigest = debugDigest;
        // Logs the latest unread-store snapshot focused on channels with unread activity.
        logDebug(SIDEBAR_UNREAD_DEBUG_TAG, 'store recompute', {
          markerCount: data.markers.length,
          nonZeroCount: nonZero.length,
          nonZero: nonZero.slice(0, MAX_DEBUG_CHANNELS),
          truncated: nonZero.length > MAX_DEBUG_CHANNELS,
        });
      }

      bumpAndNotify();
}

function scheduleRecompute(trigger) {
  if (!started || manualRecomputeQueued) return;
  manualRecomputeQueued = true;
  queueMicrotask(() => {
    manualRecomputeQueued = false;
    const seq = ++recomputeSeq;
    computeUnreadData()
      .then((data) => applyUnreadData(data, seq))
      .catch((error) => {
        logError('unreads', `recompute error (${trigger})`, error);
      });
  });
}

// Dexie terminates a liveQuery subscription permanently on the first error, which
// would freeze every unread indicator for the rest of the session; log loudly and
// restart with a short backoff instead.
function startUnreadLiveQuery() {
  liveQuery(() => {
    const seq = ++recomputeSeq;
    return computeUnreadData().then((data) => ({ data, seq }));
  }).subscribe({
    next: ({ data, seq }) => applyUnreadData(data, seq),
    error: (error) => {
      logError('unreads', 'liveQuery error', error);
      setTimeout(startUnreadLiveQuery, 1000);
    },
  });
}

function ensureStarted() {
  if (started) return;
  started = true;

  // keep changes to markers and their associated count queries in sync
  startUnreadLiveQuery();
}

// public api: overlay mark-as-read using client ulid timestamp
export function overlayMarkAsRead(channelId) {
  if (!channelId) return;
  ensureStarted();

  const prev = overlayLastReadSortOrderById.get(channelId);
  const now = ulid();

  // bail if unchanged
  if (prev && sortULIDs(now, prev) === 0) return;

  // Logs local client-side last-read overlay movement for this channel.
  logDebug('unreads', 'overlayMarkAsRead update', channelId, now);
  // Logs overlay transition with explicit previous/next sort-order values.
  logDebug(SIDEBAR_UNREAD_DEBUG_TAG, 'overlayMarkAsRead', {
    channelId,
    previous: prev || null,
    next: now,
  });
  const updated = new Map(overlayLastReadSortOrderById);
  updated.set(channelId, now);
  overlayLastReadSortOrderById = updated;

  // the overlay lives outside Dexie, so no table write will re-run the liveQuery;
  // recompute explicitly so "channel became read" applies the moment it advances.
  scheduleRecompute('overlay-mark-as-read');
  bumpAndNotify();
}

// public api: capture the divider baseline for a channel visit — the marker position
// and whether anything was unread on entry — before overlayMarkAsRead wipes the live
// counts. MessageFeed latches its unread divider from this snapshot.
export function beginChannelVisit(channelId) {
  if (!channelId) return;
  ensureStarted();

  if (!hasComputedOnce) {
    // no counts exist yet (e.g. deep link straight into a channel); the first
    // recompute fills this baseline from the server marker instead.
    pendingVisitChannelIds.add(channelId);
    return;
  }

  pendingVisitChannelIds.delete(channelId);
  const marker = markersByChannelId.get(channelId);
  const entry = {
    sortOrder: marker?.last_read_sort_order ?? null,
    hasUnread: (unreadMessagesByChannelId.get(channelId) || 0) > 0 || (unreadMentionsByChannelId.get(channelId) || 0) > 0,
  };
  entryDividerById.set(channelId, entry);

  bumpAndNotify();
}

/** Per-workspace totals for the workspace rail: mentions show a count, message-only unreads show a dot. */
export function useWorkspaceUnreadSignals(workspaceIds = []) {
  ensureStarted();

  const ids = workspaceIds;
  const cacheRef = useRef({ version: -1, ids, snapshot: { byWorkspaceId: {} } });

  return useSyncExternalStore(
    subscribe,
    () => {
      if (cacheRef.current.version === storeVersion && cacheRef.current.ids === ids) {
        return cacheRef.current.snapshot;
      }

      const requestedWorkspaceIds = new Set(ids.map(String));
      const nextByWorkspaceId = {};

      for (const marker of markersByChannelId.values()) {
        const workspaceId = String(marker.workspace_id);
        if (!requestedWorkspaceIds.has(workspaceId)) continue;

        const channelId = marker.channel_id;
        const messages = unreadMessagesByChannelId.get(channelId) || 0;
        const mentions = unreadMentionsByChannelId.get(channelId) || 0;
        if (messages <= 0 && mentions <= 0) continue;

        const signal = nextByWorkspaceId[workspaceId] || { messages: 0, mentions: 0 };
        signal.messages += messages;
        signal.mentions += mentions;
        nextByWorkspaceId[workspaceId] = signal;
      }

      // Preserve per-workspace and outer identity when values didn't change so React.memo consumers don't re-render.
      const prevByWorkspaceId = cacheRef.current.snapshot.byWorkspaceId;
      const prevKeys = Object.keys(prevByWorkspaceId);
      const nextKeys = Object.keys(nextByWorkspaceId);
      let allReused = prevKeys.length === nextKeys.length;
      for (const key of nextKeys) {
        const prev = prevByWorkspaceId[key];
        const next = nextByWorkspaceId[key];
        if (prev && prev.messages === next.messages && prev.mentions === next.mentions) {
          nextByWorkspaceId[key] = prev;
        } else {
          allReused = false;
        }
      }

      const snapshot = allReused ? cacheRef.current.snapshot : { byWorkspaceId: nextByWorkspaceId };
      cacheRef.current = { version: storeVersion, ids, snapshot };
      return snapshot;
    },
    () => ({ byWorkspaceId: {} }),
  );
}

// selector hook using external store
export function useUnreadCounts(channelIds = []) {
  ensureStarted();

  const ids = channelIds || [];
  const cacheRef = useRef({ version: -1, ids, snapshot: {} });

  return useSyncExternalStore(
    subscribe,
    () => {
      if (cacheRef.current.version !== storeVersion || cacheRef.current.ids !== ids) {
        const snapshot = { counts: {}, entryDividers: {}, __debug__: { version: storeVersion, profiles: profilesByWorkspaceId } };

        for (const id of ids) {
          // get the counts
          snapshot.counts[id] = {
            messages: unreadMessagesByChannelId.get(id) || 0,
            mentions: unreadMentionsByChannelId.get(id) || 0,
          };

          // per-visit divider baseline captured on channel entry
          const entryDivider = entryDividerById.get(id);
          if (entryDivider) snapshot.entryDividers[id] = entryDivider;

          // NOTE: not to be used directly, only for debug inspection
          snapshot.__debug__[id] = {
            server: markersByChannelId.get(id)?.last_read_sort_order,
            overlay: overlayLastReadSortOrderById.get(id),
          };
        }

        cacheRef.current = { version: storeVersion, ids, snapshot };
      }
      return cacheRef.current.snapshot;
    },
    () => ({}),
  );
}

export function useChannelUnreadSignal(channelIds = []) {
  const { counts } = useUnreadCounts(channelIds);

  return useMemo(() => {
    let messages = 0;
    let mentions = 0;

    for (const channelId of channelIds) {
      const count = counts?.[channelId];
      messages += count?.messages || 0;
      mentions += count?.mentions || 0;
    }

    if (messages <= 0 && mentions <= 0) return EMPTY_UNREAD_SIGNAL;

    return { messages, mentions };
  }, [channelIds, counts]);
}
